import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from "./api/authStore"
import { countryNames } from './config/countryNames'
import ConfirmDialog from './ConfirmDialog'
import moment from 'moment'
import WifiIcon from '@mui/icons-material/Wifi'
import LteMobiledataIcon from '@mui/icons-material/LteMobiledata'
import ComputerIcon from '@mui/icons-material/Computer'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import AndroidIcon from '@mui/icons-material/Android'
import AppleIcon from '@mui/icons-material/Apple'
import WindowIcon from '@mui/icons-material/Window'
import FlagIcon from '@mui/icons-material/Flag'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows'
import useMobile from './hooks/useMobile'
import MobileMenu from './components/MobileMenu'
import AnimatedBurgerIcon from './components/AnimatedBurgerIcon'
import MobileProxyTable from './components/MobileProxyTable'
import MobileFilters from './components/MobileFilters'
import FilterSelect, { PROXY_TYPE_FILTER_OPTIONS, PROXY_OS_FILTER_OPTIONS } from './components/FilterSelect'
import LoadingSpinner from './components/LoadingSpinner'
import PaginationActions, { PaginationRangeText } from './components/PaginationActions'

// Компонент тултипа
const Tooltip = ({ children, title }) => {
    const [show, setShow] = useState(false)
    
    return (
        <div 
            style={{ position: 'relative', display: 'inline-block' }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px'
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}

// Компонент автокомплита
const Autocomplete = ({ options = [], placeholder, onChange, defaultValue, value, getOptionLabel, inputProps = {}, showClear = true }) => {
    const [internalValue, setInternalValue] = useState(defaultValue || null)
    const [inputValue, setInputValue] = useState('')
    const [open, setOpen] = useState(false)
    const [filteredOptions, setFilteredOptions] = useState(options)
    const blurTimeoutRef = useRef(null)
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    
    // Используем внешнее значение, если оно передано
    const currentValue = value !== undefined ? value : internalValue

    useEffect(() => {
        // Если поле пустое или содержит только выбранное значение, показываем все опции
        const matchesSelection =
            currentValue != null &&
            getOptionLabel &&
            inputValue === getOptionLabel(currentValue)
        if (!inputValue || matchesSelection) {
            setFilteredOptions(options)
        } else {
            // Фильтруем опции по введенному тексту
            const filtered = options.filter(option => 
                getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
            )
            setFilteredOptions(filtered)
        }
    }, [inputValue, options, getOptionLabel, currentValue])

    // Обновляем inputValue при изменении внешнего значения
    useEffect(() => {
        if (currentValue && getOptionLabel) {
            setInputValue(getOptionLabel(currentValue))
        }
    }, [currentValue, getOptionLabel])

    // Закрытие при клике вне компонента
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (open && containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false)
            }
        }

        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [open])

    // Cleanup при размонтировании
    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current)
            }
        }
    }, [])

    const handleInputChange = (e) => {
        setInputValue(e.target.value)
        setOpen(true)
    }

    const handleInputFocus = () => {
        // Отменяем таймер закрытия, если он есть
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
        }
        setOpen(true)
        // Всегда показываем все опции при фокусе для удобства
        setFilteredOptions(options)
    }


    const handleOptionSelect = (option) => {
        if (value === undefined) {
            setInternalValue(option)
        }
        setInputValue(getOptionLabel(option))
        setOpen(false)
        onChange(null, option)
    }

    const handleClear = () => {
        if (value === undefined) {
            setInternalValue(null)
        }
        setInputValue('')
        setOpen(true)
        setFilteredOptions(options)
        onChange(null, null)
        inputRef.current?.focus()
    }
  
    return (
        <div ref={containerRef} className="autocomplete-container" style={{ position: 'relative', width: '100%' }}>
            <input
                ref={inputRef}
                type="text"
                className="form-input"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={() => {
                    blurTimeoutRef.current = setTimeout(() => setOpen(false), 200)
                }}
                style={{ paddingRight: showClear && inputValue ? '35px' : '12px' }}
                {...inputProps}
            />
            {showClear && inputValue && (
                <button
                    onClick={handleClear}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--light-gray)'
                        e.target.style.color = 'var(--text-light)'
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent'
                        e.target.style.color = 'var(--text-muted)'
                    }}
                >
                    ✕
                </button>
            )}
            {open && filteredOptions.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-sm)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: 'var(--shadow-md)'
                }}>
                    {filteredOptions.map((option, index) => (
                        <div
                            key={index}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleOptionSelect(option)}
                            style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom: index < filteredOptions.length - 1 ? '1px solid var(--light-gray)' : 'none',
                                color: 'var(--text-light)',
                                fontSize: 'var(--text-sm)'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--dark-gray)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            {getOptionLabel(option)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}


const Proxies = (props) => {
    const { getAccessTokenSilently, setToken, logout } = useAuth()
    const { isMobile, isTablet } = useMobile()
    const user = props.user // Используем props.user вместо useAuth().user
    

    // Логирование только в режиме разработки
    React.useEffect(() => {
        if (user?.email && process.env.NODE_ENV === 'development') {
        }
    }, [user?.email])

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [proxies, setProxiesList] = useState([])
    const [proxyCountries, setCountriesList] = useState([])
    const [proxyCountriesKeys, setCountriesKeysList] = useState([])
    const [afterBuy, setAfterBuy] = useState(false)

    const [country, setCountry] = useState('US')
    const [region, setRegion] = useState(null)
    const [selectedCity, setCity] = useState(null)
    const [selectedZip, setZip] = useState(null)
    const [selectedIsp, setIsp] = useState(null)
    const [selectedIp, setIp] = useState(null)
    const [selectedType, setType] = useState(null)
    const [selectedDev, setSelectedDev] = useState(null)

    const [selectedRow, setSelectedRow] = useState([])
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [isCheckingSpeed, setIsCheckingSpeed] = useState([])
    const [proxySpeed, setProxySpeed] = useState([])
    const [speedCheckTimes, setSpeedCheckTimes] = useState([])
    const [ipInputValue, setIpInputValue] = useState('')
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [loadingButton, setLoadingButton] = useState(null) // 'first', 'prev', 'next', 'last'
    // const [proxyPrice, setPrice] = React.useState(0)

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы Proxies */
            .proxies-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .proxies-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .proxies-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .proxies-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .proxies-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .proxies-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .proxies-table-container,
            :root:not([data-theme="light"]) .proxies-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // const [speed, setSpeed] = useState(null)

    // Возраст прокси из listedDays (кол-во дней в листинге)
    const calculateProxyAge = useCallback((listedDays) => {
        if (listedDays == null) return 'N/A'

        if (listedDays >= 365) return `${Math.floor(listedDays / 365)}y`
        if (listedDays >= 30) return `${Math.floor(listedDays / 30)}m`
        return `${listedDays}d`
    }, [])

    // Мемоизируем отфильтрованные данные для лучшей производительности
    const filteredProxies = useMemo(() => {
        return proxies?.proxies || []
    }, [proxies?.proxies])

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = useMemo(() => {
        return page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredProxies.length) : 0
    }, [page, rowsPerPage, filteredProxies.length])

    const handleChangePage = (event, newPage, buttonType = null) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        
        // Предотвращаем множественные клики
        if (loadingButton) return
        
        setLoadingButton(buttonType)
        setPage(validPage)
        
        // Сбрасываем выбранную строку при смене страницы
        setSelectedRow([])
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
        setIsCheckingSpeed([])
    }

    // Debounce для IP фильтра
    useEffect(() => {
        const timer = setTimeout(() => {
            if (ipInputValue === '') {
                setIp(null)
            } else {
                setIp(ipInputValue)
            }
        }, 1000)

        return () => clearTimeout(timer)
    }, [ipInputValue])

    useEffect(()=>{
        const callAsync = async() => {
            try {
                // Показываем индикатор загрузки только при смене страницы
                if (page !== 0) {
                    setLoadingButton('next')
                }
                
                // Отладочное логирование параметров только в development
                if (process.env.NODE_ENV === 'development') {
                    console.log('Filter params:', {
                        country,
                        selectedIp,
                        region,
                        selectedZip,
                        selectedCity,
                        selectedIsp,
                        selectedType,
                        selectedDev
                    })
                }
                
                // Получаем токен один раз
                const token = await getAccessTokenSilently()
                
                const proxiesPesponse = await props.apiClient.setToken(token).loadProxies(page, rowsPerPage, country, selectedIp, region, selectedZip, selectedCity, selectedIsp, selectedType, selectedDev)
                
                setProxiesList(proxiesPesponse)
                setAfterBuy(false)
                // Убираем setReloadData(true) чтобы избежать бесконечного цикла
                
                // Оптимизируем обработку массивов
                const proxiesArr = proxiesPesponse?.proxies || []
                const proxiesSpeedCheckerArray = {}
                const proxySpeedArray = {}
                const speedCheckTimesArray = {}

                // Используем for...of для лучшей производительности
                for (const row of proxiesArr) {
                    if (row?.proxyid) {
                        proxiesSpeedCheckerArray[row.proxyid] = false
                        proxySpeedArray[row.proxyid] = row?.speed?.bytesPerSec ? (row.speed.bytesPerSec / 1000 / 1000).toFixed(2) : null
                        speedCheckTimesArray[row.proxyid] = row?.speed?.checkedAt || null
                    }
                }

                setIsCheckingSpeed(proxiesSpeedCheckerArray)
                setProxySpeed(proxySpeedArray)
                setSpeedCheckTimes(speedCheckTimesArray)
                setLoadingButton(null)

            }catch(e){
                setLoadingButton(null)
            }
        }
        
        callAsync()   
      },[afterBuy, page, rowsPerPage, selectedIp, country, region, selectedCity, selectedZip, selectedIsp, selectedType, selectedDev])

    // Загружаем страны только один раз при монтировании компонента
    useEffect(()=>{
        const callAsync = async() => {
            try{
                // Получаем токен один раз
                const token = await getAccessTokenSilently()
                
                const countriesPesponse = await props.apiClient.setToken(token).getCountries()

                const countriesArr = Array.isArray(countriesPesponse?.countries) ? countriesPesponse.countries : []
                setCountriesKeysList(countriesArr.map((item) => item.code))

                // Оптимизируем создание списка стран
                const countryListSorted = countriesArr
                    .map((item) => ({
                        name: countryNames[item.code] || item.code,
                        id: item.code,
                        count: item.count,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))

                setCountriesList(countryListSorted)
            }catch(e) {
            }
        }
        callAsync()   
    },[]) // Загружаем страны только один раз при монтировании

    
    const rowClick = useCallback((row) => {
        setSelectedRow(row)
    }, [])

    const selectCountry = (cc) => {
        if(cc !== null){
            setCountry(cc.id)
        }
        
        // setSelectedCountry(countryNames[cc])
        // setSelectedCountryCode(cc)
    }

    const selectIp = (ip) => {
        if(ip == "") {
            setIp(null)
        } else {
            setIp(ip)
        }
        
    }

    const selectState = (reg) => {
        if(reg == ""){
            setRegion(null)
        } else {
            setRegion(reg)
        }
        

    }

    const selectZip = (zip) => {
        if(zip == ""){
            setZip(null)
        } else {
            setZip(zip)
        }

    }

    const selectIsp = (isp) => {
        if (isp == ""){
            setIsp(null)
        } else {
            setIsp(isp)
        }
        
    }

    const selectCity = (city) => {
        if(city == ""){
            setCity(city)
        } else {
            setCity(city)
        }
        // console.log(city)
        
    }

    const selectType = (event, newValue) => {
        console.log('selectType called with:', newValue, typeof newValue);
        if(newValue === "" || newValue === null || newValue === undefined){
            setType(null)
        } else {
            setType(newValue)
        }
    }

    const selectDevice = (event, newValue) => {
        if (newValue === '' || newValue === null || newValue === undefined) {
            setSelectedDev(null)
        } else {
            setSelectedDev(newValue)
        }
    }

    const handleBuyButtomClick = (row) => {
        // console.log('buttonclick')
        setSelectedRow(row)
        setConfirmOpen(true)
    }

    const checkSpeed = useCallback((id) => {
        const callAsync = async() => {
            try{
                setIsCheckingSpeed(prev => ({
                    ...prev,
                    [id]: true
                }))

                const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(id)
                const speed = (response?.bytesPerSec != null) ? (response.bytesPerSec / 1000 / 1000).toFixed(2) : null
                
                setProxySpeed(prev => ({
                    ...prev,
                    [id]: speed
                }))
                
                setSpeedCheckTimes(prev => ({
                    ...prev,
                    [id]: response?.checkedAt
                }))
                
                setIsCheckingSpeed(prev => ({
                    ...prev,
                    [id]: false
                }))

            }catch(e) {
                setIsCheckingSpeed(prev => ({
                    ...prev,
                    [id]: false
                }))
            }
        }
        callAsync()   
    }, [])

    
    return (
        <div className="shop-page" style={{ 
            width: isMobile ? '100%' : 'calc(100% - 280px)',
            height: '100vh',
            padding: isMobile ? '0' : '8px',
            marginLeft: isMobile ? '0' : '280px',
            overflow: isMobile ? 'hidden' : 'auto'
        }}>
            {/* Мобильная навигация */}
            {isMobile && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--spacing-md)',
                    background: 'var(--dark-gray)',
                    borderBottom: '1px solid var(--light-gray)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100
                }}>
                    <AnimatedBurgerIcon
                        isOpen={mobileMenuOpen}
                        onClick={() => setMobileMenuOpen(true)}
                    />
                    <h1 className="shop-page-title" style={{ 
                        margin: 0,
                        fontSize: 'var(--text-lg)',
                    }}>
                        Proxies Store
                    </h1>
                    <div style={{ width: '32px' }} /> {/* Spacer for centering */}
                </div>
            )}

            {/* Мобильное меню */}
            {isMobile && (
                <MobileMenu 
                    user={props.user} 
                    active={props.active} 
                    apiClient={props.apiClient}
                    isOpen={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Контент */}
            <div style={{
                height: isMobile ? 'calc(100vh - 60px)' : 'auto',
                overflow: isMobile ? 'auto' : 'visible',
                padding: isMobile ? 'var(--spacing-sm)' : '0'
            }}>
                {!isMobile && (
                    <h1 className="shop-page-title" style={{ 
                        marginTop: '8px',
                        marginBottom: '8px',
                        marginLeft: '0px',
                        fontSize: 'var(--text-2xl)',
                    }}>
                        Proxies Store
                    </h1>
                )}
            
                {/* Фильтры */}
                {isMobile ? (
                    <MobileFilters
                        proxyCountries={proxyCountries}
                        onCountryChange={selectCountry}
                        onIpChange={setIpInputValue}
                        onStateChange={selectState}
                        onCityChange={selectCity}
                        onZipChange={selectZip}
                        onIspChange={selectIsp}
                        onTypeChange={selectType}
                        onDeviceChange={selectDevice}
                        proxies={proxies}
                        ipInputValue={ipInputValue}
                        selectedType={selectedType}
                        selectedDev={selectedDev}
                        selectedCountry={country}
                        selectedState={region}
                        selectedCity={selectedCity}
                        selectedZip={selectedZip}
                        selectedIsp={selectedIsp}
                    />
                ) : (
                    <div className="shop-filters-bar" style={{
                        height: '60px',
                        marginLeft: '0px',
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            marginTop: '-3px'
                        }}>
                        {/* Страна */}
                        <div style={{ width: '190px' }}>
                        <Autocomplete 
                                    value={proxyCountries.find(c => c.id === country) || {id: 'US', name:"United States", count: 8}} 
                                    placeholder="Country"
                                    onChange={(event, value) => selectCountry(value)}
                                    options={proxyCountries||[]}
                                    getOptionLabel={(option) => option.name}
                                    inputProps={{
                                        autoComplete: "off",
                                        autoCorrect: "off",
                                        autoCapitalize: "off",
                                        spellCheck: "false",
                                        name: "location-selector",
                                        id: "location-selector",
                                        "data-form-type": "other"
                                    }}
                            />
                        </div>
                        
                        {/* IP адрес */}
                        <div style={{ width: '120px', marginLeft: '16px' }}>
                            <input
                                type="text"
                                className="form-input"
                                        placeholder="IP Address"
                                        value={ipInputValue}
                                        onChange={(e) => setIpInputValue(e.target.value)}
                                    />
                        </div>
                        
                        {/* Штат/Регион */}
                        <div style={{ width: '200px', marginLeft: '16px' }}>
                            <Autocomplete
                                    options={proxies?.options?.regions?.sort(function(a,b) {
                                        return a.localeCompare(b)
                                    })||[]}
                                    onChange={(event, value) => selectState(value)}
                                    placeholder="State"
                                getOptionLabel={(option) => option}
                            />
                        </div>
                        
                        {/* Город */}
                        <div style={{ width: '180px', marginLeft: '16px' }}>
                            <Autocomplete
                                    options={proxies?.options?.cities?.sort(function(a,b) {
                                        return a.localeCompare(b)
                                    })||[]}
                                    onChange={(event, value) => selectCity(value)}
                                    placeholder="City"
                                getOptionLabel={(option) => option}
                            />
                        </div>
                        
                        {/* ZIP код */}
                        <div style={{ width: '150px', marginLeft: '16px' }}>
                            <Autocomplete
                                options={proxies?.options?.zips?.sort(function(a,b) {
                                    return a.localeCompare(b)
                                })||[]}
                                onChange={(event, value) => selectZip(value)}
                                placeholder="Zip"
                                getOptionLabel={(option) => option}
                            />
                        </div>
                        
                        {/* ISP */}
                        <div style={{ width: '180px', marginLeft: '16px' }}>
                            <Autocomplete
                                options={proxies?.options?.isps?.sort(function(a,b) {
                                    return a.localeCompare(b)
                                })||[]}
                                onChange={(event, value) => selectIsp(value)}
                                placeholder="ISP"
                                getOptionLabel={(option) => option}
                            />
                        </div>
                        
                        {/* Тип подключения */}
                        <div style={{ width: '120px', marginLeft: '16px' }}>
                            <FilterSelect
                                options={PROXY_TYPE_FILTER_OPTIONS}
                                value={selectedType || ''}
                                onChange={(val) => selectType(null, val)}
                            />
                        </div>

                        {/* ОС / устройство */}
                        <div style={{ width: '130px', marginLeft: '16px' }}>
                            <FilterSelect
                                options={PROXY_OS_FILTER_OPTIONS}
                                value={selectedDev || ''}
                                onChange={(val) => selectDevice(null, val)}
                            />
                        </div>
                    </div>
                    </div>
                )}
                
                {/* Таблица прокси */}
                {filteredProxies.length > 0 && isMobile && (
                    <MobileProxyTable
                        proxies={proxies}
                        selectedRow={selectedRow}
                        onRowClick={rowClick}
                        onBuyClick={handleBuyButtomClick}
                        onSpeedCheck={checkSpeed}
                        isCheckingSpeed={isCheckingSpeed}
                        proxySpeed={proxySpeed}
                        speedCheckTimes={speedCheckTimes}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        loadingButton={loadingButton}
                    />
                )}
                
                {filteredProxies.length > 0 && !isMobile && (
                    <div className="table-container proxies-table-container" style={{ 
                        marginTop: 'var(--spacing-sm)', 
                        marginLeft: '0px',
                        overflowX: 'auto'
                    }}>
                        <table className="table table-proxies" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th>IP</th>
                                    <th>State</th>
                                    <th>City</th>
                                    <th>Zip</th>
                                    <th>ISP(Type)</th>
                                    <th>Users</th>
                                    <th>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center'
                                        }}>
                                            <span style={{ position: 'relative', display: 'inline-block' }}>
                                                <span 
                                                    style={{
                                                        cursor: 'help'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        const tooltip = e.target.nextElementSibling;
                                                        if (tooltip) tooltip.style.display = 'block';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const tooltip = e.target.nextElementSibling;
                                                        if (tooltip) tooltip.style.display = 'none';
                                                    }}
                                                >
                                                    Age
                                                </span>
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    backgroundColor: 'var(--medium-gray)',
                                                    color: 'var(--text-light)',
                                                    padding: '8px 10px',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--text-xs)',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 10000,
                                                    border: '1px solid var(--light-gray)',
                                                    boxShadow: 'var(--shadow-md)',
                                                    marginTop: '5px',
                                                    display: 'none',
                                                    maxWidth: '200px',
                                                    whiteSpace: 'normal',
                                                    textAlign: 'center',
                                                    pointerEvents: 'none'
                                                }}>
                                                    Proxy age
                                                </div>
                                            </span>
                                            /
                                            <span style={{ position: 'relative', display: 'inline-block' }}>
                                                <span 
                                                    style={{
                                                        cursor: 'help'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        const tooltip = e.target.nextElementSibling;
                                                        if (tooltip) tooltip.style.display = 'block';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const tooltip = e.target.nextElementSibling;
                                                        if (tooltip) tooltip.style.display = 'none';
                                                    }}
                                                >
                                                    Activity
                                                </span>
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    backgroundColor: 'var(--medium-gray)',
                                                    color: 'var(--text-light)',
                                                    padding: '8px 10px',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--text-xs)',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 10000,
                                                    border: '1px solid var(--light-gray)',
                                                    boxShadow: 'var(--shadow-md)',
                                                    marginTop: '5px',
                                                    display: 'none',
                                                    maxWidth: '200px',
                                                    whiteSpace: 'normal',
                                                    textAlign: 'center',
                                                    pointerEvents: 'none'
                                                }}>
                                                    Average proxy online time during the day
                                                </div>
                                            </span>
                                        </div>
                                    </th>
                                    <th>Speed</th>
                                    <th>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProxies.map((row) => (
                                    <tr 
                                        key={row.proxyid}
                                        onClick={() => rowClick(row)}
                                        style={{
                                            backgroundColor: selectedRow?.proxyid === row.proxyid ? 'var(--shop-row-selected-bg)' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <td>{row?.ip.replace(/\_/g, '*')}</td>
                                        <td>{row?.geo?.reg?.replace(/(.{10})..+/, "$1…")}</td>
                                        <td>{row?.geo?.city?.replace(/(.{10})..+/, "$1…")}</td>
                                        <td>{row?.geo?.zip}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span>{row?.geo?.isp?.replace(/(.{12})..+/, "$1…")}</span>
                                                {/* Иконка устройства */}
                                                {row?.os === 'windows' ? (
                                                    <WindowIcon className="table-proxies-icon" style={{ fontSize: 16, fill: 'url(#goldGradient)' }} title="Windows" />
                                                ) : row?.os === 'android' ? (
                                                    <AndroidIcon className="table-proxies-icon" style={{ fontSize: 16, fill: 'url(#goldGradient)' }} title="Android" />
                                                ) : row?.os === 'macos' ? (
                                                    <AppleIcon className="table-proxies-icon" style={{ fontSize: 16, fill: 'url(#goldGradient)' }} title="MacOS" />
                                                ) : (
                                                    <span style={{ fontSize: '16px', color: 'var(--text-muted)' }} title="Unknown device">❓</span>
                                                )}
                                                {/* Иконка типа подключения */}
                                            {row?.network === 'wifi' ? (
                                                    <WifiIcon className="table-proxies-icon" style={{ fontSize: 16, fill: 'url(#goldGradient)' }} />
                                                ):(
                                                    <LteMobiledataIcon className="table-proxies-icon" style={{ fontSize: 20, fill: 'url(#goldGradient)' }} />
                                                )}
                                            </div>
                                        </td>
                                        <td>{row?.users > 0 ? row?.users + '/4' : row?.users}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {row?.uptime !== undefined ? (
                                                <span className="shop-accent-text">
                                                    {calculateProxyAge(row?.listedDays)}/{Math.round(row?.uptime)}%
                                                </span>
                                            ):(
                                                <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                                            )}
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-outline btn-small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    checkSpeed(row?.proxyid)
                                                }}
                                                disabled={isCheckingSpeed[row?.proxyid] === true}
                                                style={{ 
                                                    minWidth: '125px',
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    opacity: isCheckingSpeed[row?.proxyid] === true ? 0.7 : 1,
                                                    cursor: isCheckingSpeed[row?.proxyid] === true ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isCheckingSpeed[row?.proxyid] === true ? (
                                                    <LoadingSpinner size={22} />
                                                ) : (
                                                    <Tooltip title={speedCheckTimes[row?.proxyid] ? moment(speedCheckTimes[row?.proxyid]).fromNow() : 'Speed test result'}>
                                                        <span>
                                                            {proxySpeed[row?.proxyid] != null ? `${proxySpeed[row?.proxyid]} Mbps` : 'Check speed'}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </button>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-buy btn-small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleBuyButtomClick(row)
                                                }}
                                                style={{ 
                                                    minWidth: '125px',
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                BUY
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {/* Пагинация */}
                        <div className="shop-table-footer" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 'var(--spacing-lg)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                    Rows per page:
                                </span>
                                <select 
                                    value={rowsPerPage}
                                    onChange={(e) => handleChangeRowsPerPage(e)}
                                    style={{
                                        borderRadius: 'var(--radius-sm)',
                                        padding: '8px 12px',
                                        fontSize: 'var(--text-sm)'
                                    }}
                                >
                                    {[10, 20, 30, 40, 50].map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                                <PaginationRangeText
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    count={proxies?.total || 0}
                                />
                                <PaginationActions 
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    count={proxies?.total || 0}
                                    onPageChange={handleChangePage}
                                    loadingButton={loadingButton}
                                    pageAction
                                />
                            </div>
                        </div>
                    </div>
                )}
                
                {filteredProxies.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-state-icon">🔍</div>
                        <h3 className="empty-state-title">No proxies found</h3>
                        <p className="empty-state-description">
                            Try adjusting your search filters to find more proxies.
                        </p>
                    </div>
                )}
                
                <ConfirmDialog 
                    open={confirmOpen} 
                    setConfirmOpen={setConfirmOpen} 
                    selectedProxy={selectedRow} 
                    apiClient={props.apiClient} 
                    setAfterBuy={setAfterBuy}
                    setReloadActive={props.setReloadActive}
                    refreshUserData={props.refreshUserData}
                />
            </div>
        </div>
    )
}

export default Proxies